import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2, Mail, Shield, Trash2, UserPlus, Users } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  competitionTeamManagersService,
  type CompetitionTeam,
} from '../../services/competition-team-managers';
import { memberAppUserUrl } from '../member-app-url';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

/**
 * Who speaks for each squad **at this competition** — its team managers.
 *
 * On the competition rather than on the circuit because that is the shape of the
 * job: the adult who travels to one stage is not the one who travels to the next,
 * so each weekend names its own (see the backend's CompetitionTeamManager). The
 * squads themselves are the season's and are written on the circuit's own page.
 *
 * Naming a manager takes an address, not an account: a squad's accompanying adult
 * usually has no DivePeaks account until somebody asks for one, and one is opened
 * and sent to them. A manager **may also be entered** here as an athlete — at a
 * club meeting the one accompanying adult is often climbing too.
 */
export default function TeamManagersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: place, loading: placeLoading, slug } = useCurrentLocation();

  const [teams, setTeams] = useState<CompetitionTeam[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Which squad's "name a manager" row is open, and what has been typed into it.
  const [namingFor, setNamingFor] = useState<string | null>(null);
  const [managerEmail, setManagerEmail] = useState('');

  const reportFailure = useCallback(
    (error: unknown) =>
      toast({
        title: error instanceof Error ? error.message : t('common.error'),
        variant: 'destructive',
      }),
    [t, toast]
  );

  const load = useCallback(
    async (competitionSlug: string) => {
      setListLoading(true);
      try {
        setTeams(await competitionTeamManagersService.listForCompetition(competitionSlug));
      } catch (error) {
        reportFailure(error);
      } finally {
        setListLoading(false);
      }
    },
    [reportFailure]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const replaceTeam = (updated: CompetitionTeam) =>
    setTeams((current) => current.map((team) => (team.id === updated.id ? updated : team)));

  const handleNameManager = async (team: CompetitionTeam) => {
    if (!slug || !managerEmail.trim()) return;
    setBusyId(team.id);
    try {
      replaceTeam(
        await competitionTeamManagersService.addManager(slug, team.id, { email: managerEmail.trim() })
      );
      setManagerEmail('');
      setNamingFor(null);
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveManager = async (team: CompetitionTeam, userId: string, name: string) => {
    if (!slug) return;
    if (!window.confirm(t('gymAdmin.teamManagers.confirmRemove', { name }))) return;

    setBusyId(team.id);
    try {
      replaceTeam(await competitionTeamManagersService.removeManager(slug, team.id, userId));
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  if (placeLoading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!place || !slug) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.teamManagers.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.teamManagers.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {listLoading && <Skeleton className="h-24 w-full" />}

          {/* No squads means nobody to name anybody on: the circuit writes its
              squads first, on its own page — or the desk does, entering people. */}
          {!listLoading && teams.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('gymAdmin.teamManagers.noTeams')}
            </p>
          )}

          {!listLoading &&
            teams.map((team) => {
              const managers = team.managers ?? [];
              return (
                <div key={team.id} className="space-y-3 rounded-lg border p-3" data-testid="team-row">
                  <div className="flex items-center gap-3">
                    <Shield className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{team.name}</span>
                    </div>
                    {/* How many of this competition's entrants are in the squad —
                        whether its managers have anybody to mark here at all. */}
                    <span
                      className="flex items-center gap-1 text-xs text-muted-foreground"
                      title={t('gymAdmin.teamManagers.entriesHere')}
                    >
                      <Users className="size-3" aria-hidden />
                      {team.entries_count ?? 0}
                    </span>
                  </div>

                  <div className="space-y-2 pl-7">
                    {managers.length === 0 && (
                      <span className="text-xs text-muted-foreground">
                        {t('gymAdmin.teamManagers.none')}
                      </span>
                    )}

                    {/* A row each rather than a chip: an admin naming somebody to
                        write other people's cards wants to see who they are —
                        the face, the handle and the address together. */}
                    {managers.map((manager) => (
                      <div
                        key={manager.user_id}
                        className="flex items-center gap-3 rounded-md border bg-muted/30 p-2"
                        data-testid="team-manager"
                      >
                        <Avatar className="size-9 border">
                          <AvatarImage
                            src={manager.avatar_small_url ?? undefined}
                            alt={manager.full_name}
                            loading="lazy"
                          />
                          <AvatarFallback className="text-xs">
                            {(manager.full_name || manager.email).charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>

                        <div className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{manager.full_name}</span>
                            {/* Invited and not signed up yet: the panel says
                                "waiting on them" rather than showing them as a
                                working manager. */}
                            {manager.pending && (
                              <Badge variant="outline" className="gap-1 text-xs">
                                <Mail className="size-3" aria-hidden />
                                {t('gymAdmin.teamManagers.invited')}
                              </Badge>
                            )}
                          </span>
                          <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                            {manager.username && <span className="truncate">@{manager.username}</span>}
                            <span className="truncate">{manager.email}</span>
                          </span>
                        </div>

                        {/* The way out to their profile on the member app. Only
                            for an account somebody has claimed: an invitation
                            nobody accepted has no public page. */}
                        {manager.slug && (
                          <Button size="icon" variant="ghost" asChild>
                            <a
                              href={memberAppUserUrl(manager.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('gymAdmin.teamManagers.viewProfile', { name: manager.full_name })}
                              aria-label={t('gymAdmin.teamManagers.viewProfile', { name: manager.full_name })}
                              data-testid="manager-profile-link"
                            >
                              <ExternalLink className="size-4" />
                            </a>
                          </Button>
                        )}

                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleRemoveManager(team, manager.user_id, manager.full_name)}
                          title={t('gymAdmin.teamManagers.remove', { name: manager.full_name })}
                          aria-label={t('gymAdmin.teamManagers.remove', { name: manager.full_name })}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    ))}

                    {namingFor === team.id ? (
                      <div className="flex w-full items-center gap-2 pt-1">
                        <Input
                          type="email"
                          value={managerEmail}
                          onChange={(e) => setManagerEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleNameManager(team)}
                          placeholder={t('gymAdmin.teamManagers.emailPlaceholder')}
                          aria-label={t('gymAdmin.teamManagers.email')}
                          autoFocus
                        />
                        <Button
                          onClick={() => handleNameManager(team)}
                          disabled={!managerEmail.trim() || busyId === team.id}
                        >
                          {busyId === team.id && <Loader2 className="mr-2 size-4 animate-spin" />}
                          {t('gymAdmin.teamManagers.name')}
                        </Button>
                        <Button variant="ghost" onClick={() => setNamingFor(null)}>
                          {t('common.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1"
                        onClick={() => {
                          setNamingFor(team.id);
                          setManagerEmail('');
                        }}
                      >
                        <UserPlus className="size-3" />
                        {t('gymAdmin.teamManagers.add')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}
